import { Component } from '@angular/core';
import { OnInit, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AlertsService } from '../../../services/alerts.service';
import { FilingService } from '../../services/filing.service';
import { UserService } from '../../../services/user.service';
import { ProductService } from '../../../services/product.service';

/**
 * Component for filing products as 'records' in records management.
 */

@Component({
	selector: 'rm-record-filing',
	templateUrl: './record-filing.component.html',
	styleUrl: './record-filing.component.css'
})
export class RecordFilingComponent implements OnInit {
	@Input() productGroup: any;

	LOCALIZATION: any;

	formFields: {
		name: string,
		label: string,
		readOnly: boolean,
		required: boolean,
		placeholder: string,
		allowedValues: any[]
	}[] = [];

	rm_folders: any[] = [];

	input: {
		rm_folder_select: string;
		rm_folder_input: string;
	} = { rm_folder_select: '', rm_folder_input: '' };

	templateMetadata: { [key: string]: any } = {};
	inprogress: boolean = false;

	constructor(
		private alertsService: AlertsService,
		private filingService: FilingService,
		private userService: UserService,
		private productService: ProductService,
		private ngbmodalService: NgbActiveModal
	) { }

	ngOnInit(): void {
		this.LOCALIZATION = this.userService.LOCALIZATION;

		let selected_lib_name = this.getSelectedLibrary();
		this.loadFormFields(selected_lib_name);
	}

	/**
	 * Determines if all products in the selected group originate 
	 * from the same library. If so, returns the source library name, otherwise undefined.
	 */
	private getSelectedLibrary(): string | undefined {
		let doc = this.productGroup.records.find((record: any) => { return this.productGroup.ids.length > 0 && record.id == this.productGroup.ids[0]; });
		let selected_lib_name = doc ? doc.library : undefined;
		if (this.productGroup.ids.length > 1) {
			for (let i = 1; i < this.productGroup.ids.length; i++) {
				doc = this.productGroup.records.find((record: any) => { return record.id == this.productGroup.ids[i]; });
				if (doc.library != selected_lib_name) {
					return undefined;
				}
			}
		}
		return selected_lib_name;
	}

	private loadFormFields(lib?: string): void {
		if (!lib) {
			this.alertsService.error({
				title: "Multiple Libraries Detected",
				message: "The record filer cannot be used with documents from multiple libraries.",
				delay: 8000
			});
			this.close();
		}

		this.formFields.push({ name: 'rm_category', label: this.LOCALIZATION.category, readOnly: false, required: false, placeholder: '', allowedValues: [] });
		this.formFields.push({ name: 'rm_subcategory', label: this.LOCALIZATION.categorySub, readOnly: false, required: false, placeholder: '', allowedValues: [] });

		this.productService.getMetadataForm(lib).then(
			(results: any) => {

				for (let i = 0; this.formFields && i < this.formFields.length; i++) {
					this.templateMetadata[this.formFields[i].name] = [""];
				}

				// TODO: DRY, put this in productService?
				this.productService.getMetadataSchema(lib).then(
					(schema: any) => {
						let controlledFields = schema.properties;
						for (let i = 0; i < this.formFields.length; i++) {
							let obj = this.formFields[i];

							let schemaObj = controlledFields[obj.name];
							if (!schemaObj) continue;
							if (schemaObj.collection && schemaObj.collection.elements) {
								obj.allowedValues = schemaObj.collection.elements;
								if (obj.allowedValues.length > 0) {
									let defaultVal = obj.allowedValues[0];
									if (defaultVal.value != "" && defaultVal.value != "0") {
										obj.allowedValues.splice(0, 0, { name: "None", value: "" });
									}
								}
							}

							this.templateMetadata[obj.name] = this.templateMetadata[this.formFields[i].name];
						}

						// Load the inital set of all open folders
						this.onChangeRMCategory();
					},
					(err: any) => {
						console.error("Failed to get the library metadata schema", err);
					}
				);

			},
			(err: any) => {
				console.error("Failed to get the library metadata form", err);
			}
		);
	}

	onChangeRMCategory(): void {
		let cid = this.templateMetadata["rm_category"][0];
		let scid = this.templateMetadata["rm_subcategory"][0];
		let returnall = false;
		if (!cid) { returnall = true; }
		this.filingService.getFoldersForCategory(cid, scid, true, returnall).then(
			(response: any) => {
				this.rm_folders = response;
				if (this.rm_folders && this.rm_folders.length > 0) {
				  this.input.rm_folder_select = this.rm_folders[0].id;
				}
			},
			(err: any) => {
				console.error("Could not retrieve available folders.", err);
			}
		);
	}

	submit(): void {
		this.inprogress = true;

		let rmData = {
			category: this.templateMetadata["rm_category"][0],
			subcategory: this.templateMetadata["rm_subcategory"][0],
			folderName: ""
		};

		if (this.input.rm_folder_select) {
			let selectedRMFolder = this.rm_folders.find((f: any) => f.id == this.input.rm_folder_select);
			rmData.folderName = selectedRMFolder ? selectedRMFolder.name : "";
		} else {
			rmData.folderName = (this.input && this.input.rm_folder_input) ? this.input.rm_folder_input : "";
		}

		this.filingService.fileRecords(this.productGroup.ids, rmData).then(
			(response: any) => {
				if (response.existingRecords.length > 0) {
					this.alertsService.info({
						message: "Skipped " + response.existingRecords.length + " document(s). These are already managed as records in the File Plan. "
					});
				}
				if (response.addedProducts.length > 0) {
					this.alertsService.success({
						message: "Filed " + response.addedProducts.length + " document(s) as new records into the File Plan."
					});
				}
				this.close();
			},
			(err: any) => {
				console.error("Error filing products to records managment.", err);
				this.alertsService.error({
					message: "Failed to file the selected documents into the File Plan. " + err
				});
				this.inprogress = false;
			}
		);
	}

	/**
	 * Closes the modal.
	 */
	close(): void {
		this.ngbmodalService.dismiss();
	}
}
